/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Glue42Core } from "@glue42/core";
import { Glue42Web } from "../../web";
import { GlueBridge } from "../communication/bridge";
import { allApplicationDefinitionsDecoder, appManagerOperationTypesDecoder, importModeDecoder, nonEmptyStringDecoder } from "../shared/decoders";
import { IoC } from "../shared/ioc";
import { LibController, ParsedConfig } from "../shared/types";
import { WindowHello } from "../windows/protocol";
import { AppsImportOperation, AppHelloSuccess, ApplicationStartConfig, AppRemoveConfig, InstanceData, operations, BaseApplicationData, AppsExportOperation, DefinitionParseResult, AppDirectoryStateChange } from "./protocol";
import {
    default as CallbackRegistryFactory,
    CallbackRegistry,
    UnsubscribeFunction,
} from "callback-registry";

export class AppManagerController implements LibController {
    private baseApplicationsTimeoutMS = 60000;
    private appImportTimeoutMS = 20;
    private readonly registry: CallbackRegistry = CallbackRegistryFactory();
    private ioc!: IoC;
    private bridge!: GlueBridge;
    private publicWindowId!: string;
    private me: Glue42Web.AppManager.Instance | undefined;
    private applications: Glue42Web.AppManager.Application[] = [];
    private instances: Glue42Web.AppManager.Instance[] = [];
    private platformRegistration!: Promise<void>;
    private logger!: Glue42Web.Logger.API;

    public handlePlatformShutdown(): void {
        this.registry.clear();
        this.applications = [];
        this.instances = [];
        delete this.me;
    }

    public async start(coreGlue: Glue42Core.GlueCore, ioc: IoC): Promise<void> {
        this.logger = coreGlue.logger.subLogger("appManger.controller.web");

        this.logger.trace("starting the web appManager controller");

        this.publicWindowId = ioc.publicWindowId;

        this.addOperationsExecutors();

        this.ioc = ioc;
        this.bridge = ioc.bridge;

        this.platformRegistration = this.registerWithPlatform();

        await this.platformRegistration;

        this.logger.trace("registration with the platform successful, attaching the appManager property to glue and returning");

        const api = this.toApi();

        (coreGlue as Glue42Web.API).appManager = api;
    }

    public async handleBridgeMessage(args: any): Promise<void> {
        await this.platformRegistration;

        const operationName = appManagerOperationTypesDecoder.runWithException(args.operation);

        const operation = operations[operationName];

        if (!operation.execute) {
            return;
        }

        let operationData: any = args.data;

        if (operation.dataDecoder) {
            operationData = operation.dataDecoder.runWithException(args.data);
        }

        return await operation.execute(operationData);
    }

    public onInstanceStarted(callback: (instance: Glue42Web.AppManager.Instance) => any): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("onInstanceStarted requires a single argument of type function");
        }

        return this.registry.add("instance-started", callback, this.instances);
    }

    public onInstanceStopped(callback: (instance: Glue42Web.AppManager.Instance) => any): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("onInstanceStopped requires a single argument of type function");
        }

        return this.registry.add("instance-stopped", callback);
    }

    public async startApplication(appName: string, context?: object, options?: Glue42Web.AppManager.ApplicationStartOptions): Promise<Glue42Web.AppManager.Instance> {
        // reuseId is a hidden property for workspaces-only use
        // layoutComponentId is a hidden property for global layouts
        // forceChromeTab is a hidden property for extension use only
        const startOptions: ApplicationStartConfig = {
            name: appName,
            waitForAGMReady: options?.waitForAGMReady ?? true,
            context,
            top: options?.top,
            left: options?.left,
            width: options?.width,
            height: options?.height,
            relativeTo: options?.relativeTo,
            relativeDirection: options?.relativeDirection,
            id: (options as any)?.reuseId,
            forceChromeTab: (options as any)?.forceTab,
            layoutComponentId: (options as any)?.layoutComponentId
        };

        const openResult = await this.bridge.send<ApplicationStartConfig, InstanceData>("appManager", operations.applicationStart, startOptions);

        const app = this.applications.find((a) => a.name === openResult.applicationName) as Glue42Web.AppManager.Application;

        return this.ioc.buildInstance(openResult, app);
    }

    public getApplication(name: string): Glue42Web.AppManager.Application {
        const verifiedName = nonEmptyStringDecoder.runWithException(name);

        return this.applications.find((app) => app.name === verifiedName) as Glue42Web.AppManager.Application;
    }

    public getInstances(): Glue42Web.AppManager.Instance[] {
        return this.instances.slice();
    }

    private toApi(): Glue42Web.AppManager.API {
        const api: Glue42Web.AppManager.API = {
            myInstance: this.me as unknown as Glue42Web.AppManager.Instance,
            inMemory: {
                import: this.import.bind(this),
                remove: this.remove.bind(this),
                export: this.export.bind(this),
                clear: this.clear.bind(this)
            },
            application: this.getApplication.bind(this),
            applications: this.getApplications.bind(this),
            instances: this.getInstances.bind(this),
            onAppAdded: this.onAppAdded.bind(this),
            onAppChanged: this.onAppChanged.bind(this),
            onAppRemoved: this.onAppRemoved.bind(this),
            onInstanceStarted: this.onInstanceStarted.bind(this),
            onInstanceStopped: this.onInstanceStopped.bind(this)
        };

        return api;
    }

    private addOperationsExecutors(): void {
        operations.appDirectoryStateChange.execute = this.handleAppDirectoryStateChange.bind(this);
        operations.instanceStarted.execute = this.handleInstanceStartedMessage.bind(this);
        operations.instanceStopped.execute = this.handleInstanceStoppedMessage.bind(this);
    }

    private async handleAppDirectoryStateChange(data: AppDirectoryStateChange): Promise<void> {
        data.appsAdded.forEach(this.handleApplicationAddedMessage.bind(this));
        data.appsChanged.forEach(this.handleApplicationChangedMessage.bind(this));
        data.appsRemoved.forEach(this.handleApplicationRemovedMessage.bind(this));
    }

    private onAppAdded(callback: (app: Glue42Web.AppManager.Application) => any): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("onAppAdded requires a single argument of type function");
        }

        return this.registry.add("application-added", callback, this.applications);
    }

    private onAppRemoved(callback: (app: Glue42Web.AppManager.Application) => any): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("onAppRemoved requires a single argument of type function");
        }

        return this.registry.add("application-removed", callback);
    }

    private onAppChanged(callback: (app: Glue42Web.AppManager.Application) => any): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("onAppChanged requires a single argument of type function");
        }

        return this.registry.add("application-changed", callback);
    }

    private async handleApplicationAddedMessage(appData: BaseApplicationData): Promise<void> {

        if (this.applications.some((app) => app.name === appData.name)) {
            return;
        }

        const app = await this.ioc.buildApplication(appData, []);

        const instances = this.instances.filter((instance) => instance.application.name === app.name);

        app.instances.push(...instances);

        this.applications.push(app);

        this.registry.execute("application-added", app);
    }

    private async handleApplicationRemovedMessage(appData: BaseApplicationData): Promise<void> {
        const appIndex = this.applications.findIndex((app) => app.name === appData.name);

        if (appIndex < 0) {
            return;
        }

        const app = this.applications[appIndex];

        this.applications.splice(appIndex, 1);

        this.registry.execute("application-removed", app);
    }

    private async handleApplicationChangedMessage(appData: BaseApplicationData): Promise<void> {
        const app = this.applications.find((app) => app.name === appData.name);

        if (!app) {
            return this.handleApplicationAddedMessage(appData);
        }

        app.title = appData.title as string;
        app.version = appData.version as string;
        app.icon = appData.icon as string;
        app.caption = appData.caption as string;
        app.userProperties = appData.userProperties;

        this.registry.execute("application-changed", app);
    }

    private async handleInstanceStartedMessage(instanceData: InstanceData): Promise<void> {
        if (this.instances.some((instance) => instance.id === instanceData.id)) {
            return;
        }

        const application = this.applications.find((app) => app.name === instanceData.applicationName);

        if (!application) {
            throw new Error(`Cannot add instance: ${instanceData.id}, because there is no application definition associated with it`);
        }

        const instance = this.ioc.buildInstance(instanceData, application);

        this.instances.push(instance);
        application.instances.push(instance);

        this.registry.execute("instance-started", instance);
    }

    private async handleInstanceStoppedMessage(instanceData: InstanceData): Promise<void> {
        const instance = this.instances.find((i) => i.id === instanceData.id);

        if (instance) {
            const instIdx = this.instances.findIndex((inst) => inst.id === instanceData.id);
            this.instances.splice(instIdx, 1);
        }

        const application = this.applications.find((app) => app.instances.some((inst) => inst.id === instanceData.id));

        if (application) {
            const instIdxApps = application.instances.findIndex((inst) => inst.id === instanceData.id);
            application.instances.splice(instIdxApps, 1);
        }

        if (!instance) {
            return;
        }

        this.registry.execute("instance-stopped", instance);
    }

    private async import(definitions: Glue42Web.AppManager.Definition[], mode: "replace" | "merge" = "replace"): Promise<Glue42Web.AppManager.ImportResult> {
        importModeDecoder.runWithException(mode);

        if (!Array.isArray(definitions)) {
            throw new Error("Import must be called with an array of definitions");
        }

        if (definitions.length > 10000) {
            throw new Error("Cannot import more than 10000 app definitions in Glue42 Core.");
        }

        const parseResult = definitions.reduce<DefinitionParseResult>((soFar, definition) => {

            const decodeResult = allApplicationDefinitionsDecoder.run(definition);

            if (!decodeResult.ok) {
                soFar.invalid.push({ app: definition?.name, error: JSON.stringify(decodeResult.error) });
            } else {
                soFar.valid.push(definition);
            }

            return soFar;
        }, { valid: [], invalid: [] });

        const responseTimeout = this.baseApplicationsTimeoutMS + this.appImportTimeoutMS * parseResult.valid.length;

        await this.bridge.send<AppsImportOperation, void>("appManager", operations.import, { definitions: parseResult.valid, mode }, { methodResponseTimeoutMs: responseTimeout });

        return {
            imported: parseResult.valid.map((valid) => valid.name),
            errors: parseResult.invalid
        };
    }

    private async remove(name: string): Promise<void> {
        nonEmptyStringDecoder.runWithException(name);

        await this.bridge.send<AppRemoveConfig, void>("appManager", operations.remove, { name }, { methodResponseTimeoutMs: this.baseApplicationsTimeoutMS });
    }

    private async clear(): Promise<void> {
        await this.bridge.send<void, void>("appManager", operations.clear, undefined, { methodResponseTimeoutMs: this.baseApplicationsTimeoutMS });
    }

    private async export(): Promise<Glue42Web.AppManager.Definition[]> {

        const response = await this.bridge.send<void, AppsExportOperation>("appManager", operations.export, undefined, { methodResponseTimeoutMs: this.baseApplicationsTimeoutMS });

        return response.definitions;
    }

    public getApplications(): Glue42Web.AppManager.Application[] {
        return this.applications.slice();
    }

    private async registerWithPlatform(): Promise<void> {
        const result = await this.bridge.send<WindowHello, AppHelloSuccess>("appManager", operations.appHello, { windowId: this.publicWindowId }, { methodResponseTimeoutMs: this.baseApplicationsTimeoutMS });

        this.logger.trace("the platform responded to the hello message with a full list of apps");

        this.applications = await Promise.all(result.apps.map((app) => this.ioc.buildApplication(app, app.instances)));

        this.instances = this.applications.reduce<Glue42Web.AppManager.Instance[]>((instancesSoFar, app) => {

            instancesSoFar.push(...app.instances);

            return instancesSoFar;
        }, []);

        this.me = this.findMyInstance();

        this.logger.trace(`all applications were parsed and saved. I am ${this.me ? "NOT a" : "a"} valid instance`);
    }

    private findMyInstance(): Glue42Web.AppManager.Instance | undefined {
        for (const app of this.applications) {
            const foundInstance = app.instances.find((instance) => instance.id === this.publicWindowId);

            if (foundInstance) {
                return foundInstance;
            }
        }
    }
}
